// Subscription Sync Service
// Persists subscription state to Supabase so it survives localStorage clears.
// Called after Stripe payment verification and on app startup to restore.

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import { appSubscriptionTierFromStored } from '../utils/subscriptionTier';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

// ─── Types ───────────────────────────────────────

export interface SubscriptionRecord {
  user_id: string;
  tier: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  plan_months: number;
  started_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

// ─── Sync to Supabase (after Stripe payment) ────

/**
 * Upserts subscription data to Supabase after successful Stripe verification.
 * Called from paymentService.handlePaymentSuccess().
 */
export async function syncSubscriptionToSupabase(
  userId: string,
  tier: string,
  stripeSubId: string | null,
  stripeCustomerId: string | null,
  months: number
): Promise<boolean> {
  try {
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setMonth(expiresAt.getMonth() + months);

    const { error } = await supabase
      .from('user_subscriptions')
      .upsert({
        user_id: userId,
        tier,
        stripe_subscription_id: stripeSubId,
        stripe_customer_id: stripeCustomerId,
        plan_months: months,
        started_at: now.toISOString(),
        expires_at: expiresAt.toISOString(),
        is_active: true,
        updated_at: now.toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      logger.error('syncSubscriptionToSupabase error:', error);
      return false;
    }

    logger.info(`Subscription synced to Supabase: ${tier} for ${months} months`);
    return true;
  } catch (err) {
    logger.error('syncSubscriptionToSupabase exception:', err);
    return false;
  }
}

// ─── Load from Supabase (restore on startup) ────

/**
 * Loads subscription from Supabase and restores to localStorage if missing.
 * Called on app startup when user is authenticated but localStorage is empty.
 */
export async function loadSubscriptionFromSupabase(userId: string): Promise<SubscriptionRecord | null> {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      // No subscription found — user is on free tier
      return null;
    }

    // Check if still active (not expired)
    if (data.expires_at) {
      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) {
        // Subscription expired — mark inactive in DB
        await supabase
          .from('user_subscriptions')
          .update({ is_active: false })
          .eq('user_id', userId);

        logger.info('Subscription expired, marked inactive');
        return { ...data, is_active: false };
      }
    }

    // Restore to localStorage (normalize legacy pro/go → monthly for app tier model)
    if (data.is_active && data.tier !== 'free') {
      const appTier = appSubscriptionTierFromStored(data.tier);
      if (appTier !== 'free') {
        localStorage.setItem('subscriptionTier', appTier);
        localStorage.setItem('subscriptionActive', 'true');
        if (data.stripe_subscription_id) {
          localStorage.setItem('stripeSubscriptionId', data.stripe_subscription_id);
        }
        if (data.expires_at) {
          localStorage.setItem('subscriptionExpires', data.expires_at);
        }
        logger.info(`Subscription restored from Supabase: ${data.tier} → ${appTier}`);
      }
    }

    return data as SubscriptionRecord;
  } catch (err) {
    logger.error('loadSubscriptionFromSupabase exception:', err);
    return null;
  }
}

// ─── Check active status ─────────────────────────

/**
 * Quick check: is the user's subscription currently active?
 * Checks Supabase directly (not localStorage).
 */
export async function checkSubscriptionActive(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('is_active, expires_at')
      .eq('user_id', userId)
      .single();

    if (error || !data) return false;

    // Check expiry
    if (data.expires_at) {
      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) return false;
    }

    return data.is_active === true;
  } catch {
    return false;
  }
}
